import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { thumb } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Dashboard() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState({ classes: [], studio_bookings: [], open_studio_bookings: [] });
  const [tutorials, setTutorials] = useState([]);
  const [classInfo, setClassInfo] = useState({});
  const [membership, setMembership] = useState(null);

  useEffect(() => {
    if (!user || user === false) return;
    api.get("/me/bookings").then(({ data }) => setBookings(data));
    api.get("/me/tutorials").then(({ data }) => setTutorials(data));
    api.get("/membership/me").then(({ data }) => setMembership(data)).catch(() => {});
    api.get("/classes").then(({ data }) => {
      const m = {};
      data.forEach((c) => (m[c.id] = c));
      setClassInfo(m);
    });
  }, [user]);

  if (!user || user === false) return null;

  return (
    <div data-testid="dashboard-page" className="pt-32 pb-24 fade-in">
      <div className="max-w-6xl mx-auto px-6 sm:px-12 lg:px-16">
        <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#121212] mb-4">Your library</p>
        <h1 className="font-serif text-5xl text-stone-900">Hi, {user.name}.</h1>
        {user.role === "creator" && (
          <Link to="/creator/edit" data-testid="edit-profile-link" className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-[#121212] text-[#F2E9D8] hover:bg-black text-xs tracking-[0.25em] uppercase font-bold">
            Edit your creator profile →
          </Link>
        )}

        <div className="mt-8" data-testid="membership-banner">
          {membership?.active ? (
            <div className="bg-[#121212] text-[#F2E9D8] p-6 flex justify-between items-center flex-wrap gap-4">
              <div>
                <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#C6FF33]">Cake Society Member</p>
                <p className="mt-1 text-sm">Free Open Studio visits left this month: <strong>{membership.free_visits_remaining}</strong></p>
              </div>
              <Link to="/open-studio" className="px-6 py-3 bg-[#C6FF33] text-[#121212] hover:bg-[#b6ef23] text-xs tracking-[0.25em] uppercase font-bold">Book Open Studio →</Link>
            </div>
          ) : (
            <div className="bg-white border-2 border-[#121212] p-6 flex justify-between items-center flex-wrap gap-4">
              <div>
                <p className="text-xs tracking-[0.3em] uppercase font-bold">Not a member yet</p>
                <p className="mt-1 text-sm text-[#121212]/70">Get a free monthly studio visit + 15% off everything.</p>
              </div>
              <Link to="/membership" className="px-6 py-3 bg-[#121212] text-[#F2E9D8] hover:bg-black text-xs tracking-[0.25em] uppercase font-bold">Join now →</Link>
            </div>
          )}
        </div>

        <section className="mt-16">
          <h2 className="font-serif font-black text-3xl text-[#121212] mb-6">Upcoming classes</h2>
          {bookings.classes.length === 0 ? (
            <p className="text-stone-500">No bookings yet. <Link to="/classes" className="text-[#121212] hover:underline">Browse classes →</Link></p>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {bookings.classes.map((b) => {
                const c = classInfo[b.item_id];
                if (!c) return null;
                return (
                  <div key={b.id} data-testid={`booking-${b.id}`} className="p-6 rounded-2xl bg-white border border-stone-200 flex gap-4">
                    <img src={thumb(c.image_url, 200)} alt="" className="w-20 h-20 rounded-xl object-cover" />
                    <div>
                      <p className="text-xs tracking-[0.2em] uppercase text-[#121212]">{c.date}</p>
                      <h3 className="font-serif text-xl text-stone-900">{c.title}</h3>
                      <p className="text-stone-500 text-sm">{c.duration} · {c.location}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section className="mt-16">
          <h2 className="font-serif font-black text-3xl text-[#121212] mb-6">My Tutorials &amp; Kits</h2>
          {tutorials.length === 0 ? (
            <p className="text-stone-500">No tutorials yet. <Link to="/tutorials" className="text-[#121212] hover:underline">Browse tutorials →</Link></p>
          ) : (
            <div className="grid md:grid-cols-3 gap-4">
              {tutorials.map((t) => (
                <Link key={t.id} to={`/tutorials/${t.id}`} data-testid={`my-tut-${t.id}`} className="rounded-2xl overflow-hidden border border-stone-200 hover:shadow-lg transition-all">
                  <img src={thumb(t.image_url, 500)} alt={t.title} className="w-full aspect-video object-cover" />
                  <div className="p-4">
                    <h3 className="font-serif text-lg text-stone-900">{t.title}</h3>
                    <p className="text-stone-500 text-sm">{t.duration}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {bookings.open_studio_bookings?.length > 0 && (
          <section className="mt-16">
            <h2 className="font-serif font-black text-3xl text-[#121212] mb-6">Open Studio visits</h2>
            <div className="space-y-3">
              {bookings.open_studio_bookings.map((s) => (
                <div key={s.id} data-testid={`os-booking-${s.id}`} className="p-5 rounded-xl bg-white border border-stone-200 flex justify-between flex-wrap gap-2">
                  <div>
                    <p className="font-medium text-stone-900">{s.booking_date} · {s.time_slot}</p>
                    <p className="text-stone-500 text-sm">{s.project}</p>
                  </div>
                  <span className="px-3 py-1 rounded-none text-xs h-fit bg-emerald-100 text-emerald-800">
                    {s.is_free ? "Free visit" : "Paid"}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {bookings.studio_bookings.length > 0 && (
          <section className="mt-16">
            <h2 className="font-serif font-black text-3xl text-[#121212] mb-6">Studio bookings</h2>
            <div className="space-y-3">
              {bookings.studio_bookings.map((s) => (
                <div key={s.id} data-testid={`studio-booking-${s.id}`} className="p-5 rounded-xl bg-white border border-stone-200 flex justify-between flex-wrap gap-2">
                  <div>
                    <p className="font-medium text-stone-900">{s.booking_date} · {s.time_slot}</p>
                    <p className="text-stone-500 text-sm">{s.purpose}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-none text-xs h-fit ${
                    s.status === "paid" ? "bg-emerald-100 text-emerald-800" :
                    s.status === "pending_payment" ? "bg-amber-100 text-amber-800" :
                    "bg-stone-100 text-stone-700"
                  }`}>{s.status.replace(/_/g, " ")}</span>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
